import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BoxV2 as Box,
  Table,
  TableFilterV2 as TableFilter,
  useTable,
  useFilters,
  FilterType,
  Badge,
  Flex,
  Drawer,
} from 'portal-commons';

import PageContainer from '@/components/PageContainer';
import PageTitle from '@/components/PageTitle';
import { senderIdInventoryController, SenderId } from '@/apis/core';
import { humanize, humanizeSenderIdType } from '@/utils/string';
import { Loader } from '@/shared_elements';
import { ROUTES } from '@/constants/paths';
import { toastFlashMessage } from '@/utils';
import MoreButton from '@/components/MoreButton';
import SuspendSenderIdForm from '../components/SuspendSenderIdForm';

const TABLE_HEAD_ROWS = [
  { id: 'senderIdValue', label: 'SENDER ID', sortable: false },
  { id: 'senderIdType', label: 'SENDER ID TYPE', sortable: false },
  { id: 'status', label: 'SENDER ID STATUS', sortable: false },
  { id: 'dcaName', label: 'ASSIGNED DCA', sortable: false },
  { id: 'intlInbound', label: `INT'L INBOUND A2P SMS`, sortable: false },
  { id: 'intlOutbound', label: `INT'L OUTBOUND A2P SMS`, sortable: false },
  { id: 'onNetInbound', label: 'ON-NET INBOUND', sortable: false },
  {
    id: 'offNetInbound',
    label: 'OFF-NET INBOUND',
    sortable: false,
  },
  {
    id: 'offNetOutbound',
    label: 'OFF-NET OUTBOUND',
    sortable: false,
  },
  { id: 'campaignUid', label: 'PROGRAM ID', sortable: false },
  { id: 'actions', label: '', sortable: false },
];

const FILTER_CONFIG = {
  senderIdValue: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Sender ID',
    placeholder: 'Search Sender ID',
  },
};

const SENDER_ID_STATUS_BADGE_MAP: Record<string, 'success' | 'info' | 'error'> =
  {
    AVAILABLE: 'success',
    ASSIGNED: 'info',
    SUSPENDED: 'error',
  };

const MAX_DISPLAY_RECORDS = 10000;

const SenderIdsListing: React.FC = () => {
  const [suspendingSenderId, setSuspendingSenderId] = useState<SenderId | null>(
    null
  );

  const {
    configs: filterConfigs,
    appliedFilters,
    candidateFilters,
    additionalFilters,
    handleEdit,
    handleApply,
    setOptions,
  } = useFilters({
    configs: FILTER_CONFIG,
  });

  const handleDrawerClose = () => {
    setSuspendingSenderId(null);
  };

  const handleSuspendApply = async (explanation: string) => {
    if (suspendingSenderId) {
      const response = await senderIdInventoryController.suspendSenderId(
        suspendingSenderId.uid,
        {
          status: 'SUSPENDED',
          message: explanation,
        }
      );
      if (response) {
        toastFlashMessage(
          `Sender ID ${suspendingSenderId.senderIdValue} successfully suspended.`,
          'success'
        );
        setSuspendingSenderId(null);
        table.reload();
      }
    }
  };

  useEffect(() => {
    const fetchSenderIdOptions = async () => {
      const response = await senderIdInventoryController.getSenderIds({
        page: 1,
        recordsPerPage: 10000,
      });

      if (response?.records) {
        const options = response.records
          .map((item) => ({
            label: item.senderIdValue,
            value: item.senderIdValue,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setOptions('senderIdValue', options);
      }
    };

    fetchSenderIdOptions();
  }, []);

  const table = useTable<SenderId>(
    async (): Promise<{
      data: SenderId[];
      total: number;
      rowsPerPage: number;
      page: number;
    }> => {
      const queries = {
        ...table.filter,
        ...appliedFilters,
        ...additionalFilters,
      };
      const response = await senderIdInventoryController.getSenderIds(queries);
      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        rowsPerPage: response?.recordsPerPage || 10,
        page: response?.page || 1,
      };
    },
    {
      cappingTotal: MAX_DISPLAY_RECORDS,
      deps: [appliedFilters, additionalFilters],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const tableData = useMemo(() => {
    const renderBoolean = (val: boolean | undefined) => (
      <span>{val ? 'Yes' : 'No'}</span>
    );

    const senderIdStatus = (status: string) => {
      const type = SENDER_ID_STATUS_BADGE_MAP[status] || 'info';
      return (
        <Badge
          size="md"
          shape="rectangle"
          variant="fill"
          type={type}
          data-testid="senderIdStatusBadge"
        >
          {humanize(status)}
        </Badge>
      );
    };

    return table.tableData.map((data: SenderId) => ({
      rawUid: data.uid,
      senderIdValue: (
        <Box sx={{ width: 225, wordBreak: 'break-all' }}>
          {data.senderIdValue}
        </Box>
      ),
      senderIdType: <span>{humanizeSenderIdType(data.senderIdType)}</span>,
      status: senderIdStatus(data.status),
      dcaName: <span>{data.dcaName || 'N/A'}</span>,
      campaignUid: data.campaignUid ? (
        <Link
          to={ROUTES.PROGRAM_DETAILS.replace(':id', data.campaignUid)}
          style={{
            color: '#4D5659',
            textDecoration: 'underline',
          }}
          data-testid="programDetailsLink"
        >
          {data.campaignUid}
        </Link>
      ) : (
        <span>N/A</span>
      ),
      intlInbound: renderBoolean(data.internationalInbound),
      intlOutbound: renderBoolean(data.internationalOutbound),
      onNetInbound: renderBoolean(data.onNetInbound),
      offNetInbound: renderBoolean(data.offNetInbound),
      offNetOutbound: renderBoolean(data.offNetOutbound),
      actions: (
        <Flex sx={{ justifyContent: 'flex-end' }}>
          <MoreButton
            data={data}
            items={[
              {
                label: 'Suspend Sender ID',
                disabled: data.status === 'SUSPENDED',
                onClick: () => {
                  setSuspendingSenderId(data);
                },
              },
              // {
              //   label: 'Assign DCA',
              //   disabled: ['ASSIGNED', 'SUSPENDED'].includes(data.status),
              //   onClick: () => {
              //     console.log('Assign DCA');
              //   },
              // },
            ]}
          />
        </Flex>
      ),
    }));
  }, [table.tableData]);

  if (table.pageLoading) return <Loader />;

  return (
    <PageContainer sx={{ px: 96, pt: 40, rowGap: null }}>
      <PageTitle note="Results capped at first 10,000 records">
        Sender ID Management
      </PageTitle>

      <Box sx={{ mt: 'xl', mb: 'm', width: '100%' }}>
        <TableFilter
          configs={filterConfigs}
          candidateValues={candidateFilters}
          appliedValues={appliedFilters}
          onCandidateValuesChange={handleEdit}
          onAppliedValuesChange={handleApply}
          data-testid="tableFilters"
        />
      </Box>

      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          '& table': {
            '& th:last-child': { minWidth: '20px' },
            '& td::nth-of-type(3), td:last-child': { py: 'unset' },
          },
        }}
      >
        <Table
          disableHover
          loading={table.loading}
          headRows={TABLE_HEAD_ROWS}
          emptyState="No Sender IDs found."
          data={tableData}
          rowKey="rawUid"
          handleChangePage={table.handleChangePage}
          createSortHandler={table.handleSorting}
          filter={table.filter}
          pagination={table.pagination}
          emptyBlockHeight="180px"
          data-testid="senderIdsTable"
        />
      </Box>

      <Drawer
        open={suspendingSenderId !== null}
        onClose={handleDrawerClose}
        width="500px"
      >
        {suspendingSenderId && (
          <SuspendSenderIdForm
            senderId={suspendingSenderId}
            onCancel={handleDrawerClose}
            onSubmit={handleSuspendApply}
          />
        )}
      </Drawer>
    </PageContainer>
  );
};

export default SenderIdsListing;
